<template>
  <div>
    <h3 class="mb-2">
      {{ selectedService.name }}
    </h3>

    <div>
      <b-media
        no-body
        class="mb-50"
      >
        <b-avatar
          variant="light-primary"
          rounded
          class="mr-1"
        >
          <feather-icon
            icon="ClockIcon"
            size="18"
          />
        </b-avatar>
        <h6 class="align-self-center my-auto">
          {{ humanizeDuration(selectedService.duration) }}
        </h6>
      </b-media>

      <b-media
        v-if="tutorOptions.length"
        no-body
        class="mb-50"
      >
        <b-avatar
          variant="light-primary"
          rounded
          class="mr-1"
        >
          <feather-icon
            icon="UserIcon"
            size="18"
          />
        </b-avatar>

        <div v-if="!isTutorSelectDisabled">
          <b-form-group
            v-if="hasMultipleTutors"
            class="mb-0"
          >
            <b-form-select
              id="service-tutor-select"
              :value="selectedTutorId"
              :options="tutorOptions"
              :disabled="isTutorSelectDisabled"
              @input="updateTutor"
            />
          </b-form-group>
        </div>
        <h6
          v-else
          class="align-self-center my-auto"
        >
          {{ selectedTutorLabel || '-' }}
        </h6>
      </b-media>

      <b-media
        no-body
        class="mb-50"
      >
        <b-avatar
          variant="light-primary"
          rounded
          class="mr-1"
        >
          <feather-icon
            icon="MapPinIcon"
            size="18"
          />
        </b-avatar>
        <div v-if="locationOptions.length && !isLocationSelectDisabled">
          <b-form-group
            v-if="hasMultipleLocations"
            label-class="font-weight-bolder"
            class="mb-0"
          >
            <b-form-select
              id="service-location-select"
              :value="selectedLocation"
              :options="locationOptions"
              :disabled="isLocationSelectDisabled"
              @input="updateLocation"
            />
            <b-form-text
              v-if="requiresLocationSelection && !parametersConfirmed && !isTutorReady"
              class="mt-25"
            >
              {{ $t('settings.profile.reservation.select-tutor-first') }}
            </b-form-text>
          </b-form-group>
        </div>
        <h6
          v-else
          class="align-self-center my-auto"
        >
          {{ selectedLocation ? $t(`shared.service-location.${selectedLocation}`) : '-' }}
        </h6>
      </b-media>

      <b-alert
        v-if="parametersConfirmed && 0"
        show
        variant="light-warning"
        class="mb-50 p-50"
      >
        {{ $t('settings.profile.reservation.parameters-locked-info') }}
      </b-alert>

      <b-media
        v-for="(selectedSpot, spotKey) in selectedSpots"
        :key="`selected-spot-${spotKey}`"
        no-body
        class="mb-50"
      >
        <b-avatar
          variant="light-primary"
          rounded
          class="mr-1"
        >
          <feather-icon
            icon="CalendarIcon"
            size="18"
          />
        </b-avatar>
        <h6 class="align-self-center my-auto">
          <feather-icon
            icon="XIcon"
            class="text-danger ml-50 float-right cursor-pointer"
            @click="$emit('remove-spot', spotKey)"
          />

          {{ dayjs(selectedSpot.startAt).format('dddd, LL') }}
          <span class="text-nowrap">
            {{ dayjs(selectedSpot.startAt).format('LT') }}
            - {{ dayjs(selectedSpot.endAt).format('LT') }}
          </span>
        </h6>
      </b-media>

      <b-media
        v-if="0"
        no-body
        class="mb-50"
      >
        <b-avatar
          variant="light-primary"
          rounded
          class="mr-1"
        >
          <feather-icon
            icon="GlobeIcon"
            size="18"
          />
        </b-avatar>
        <h6 class="align-self-center my-auto">
          <!--          {{ tz }}-->
        </h6>
      </b-media>
    </div>
  </div>
</template>
<script>
import dayjs from 'dayjs'
import Ripple from 'vue-ripple-directive'
import { humanizeDuration } from '@/helpers/dateTimeHelper'

export default {
  directives: {
    Ripple,
  },
  props: {
    selectedService: {
      type: Object,
      default: () => {},
    },
    selectedSpots: {
      type: Array,
      default: () => [],
    },
    selectedLocation: {
      type: String,
      default: null,
    },
    selectedTutorId: {
      type: [Number, String, null],
      default: null,
    },
    selectedTutor: {
      type: Object,
      default: () => null,
    },
    requiresTutorSelection: {
      type: Boolean,
      default: false,
    },
    requiresLocationSelection: {
      type: Boolean,
      default: false,
    },
    isTutorReady: {
      type: Boolean,
      default: false,
    },
    parametersConfirmed: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasMultipleLocations() {
      return Array.isArray(this.selectedService?.serviceLocation) && this.selectedService.serviceLocation.length > 1
    },
    locationOptions() {
      if (!Array.isArray(this.selectedService?.serviceLocation)) {
        return []
      }

      const options = this.selectedService.serviceLocation.map(location => ({
        value: location,
        text: this.$t(`shared.service-location.${location}`),
      }))

      if (this.requiresLocationSelection && !this.parametersConfirmed) {
        return [{ value: null, text: this.$t('settings.profile.reservation.select-location') }, ...options]
      }

      return options
    },
    hasMultipleTutors() {
      return Array.isArray(this.selectedService?.bookableTutors) && this.selectedService.bookableTutors.length > 1
    },
    tutorOptions() {
      if (!Array.isArray(this.selectedService?.bookableTutors)) {
        return []
      }

      const options = this.selectedService.bookableTutors.map(tutor => ({
        value: tutor.id,
        text: this.humanizeTutor(tutor),
      }))

      if (this.requiresTutorSelection && !this.parametersConfirmed) {
        return [{ value: null, text: this.$t('settings.profile.reservation.select-tutor') }, ...options]
      }

      return options
    },
    selectedTutorLabel() {
      if (this.selectedTutor) {
        return this.humanizeTutor(this.selectedTutor)
      }

      const fromService = Array.isArray(this.selectedService?.bookableTutors)
        ? this.selectedService.bookableTutors.find(tutor => tutor.id === this.selectedTutorId)
        : null

      return fromService ? this.humanizeTutor(fromService) : null
    },
    tutorPlaceholder() {
      return this.requiresTutorSelection ? this.$t('settings.profile.reservation.select-tutor') : null
    },
    locationPlaceholder() {
      return this.requiresLocationSelection ? this.$t('settings.profile.reservation.select-location') : null
    },
    isTutorSelectDisabled() {
      return this.parametersConfirmed
    },
    isLocationSelectDisabled() {
      if (this.parametersConfirmed) {
        return true
      }
      if (this.requiresLocationSelection && !this.isTutorReady) {
        return true
      }
      return false
    },
  },
  methods: {
    humanizeDuration,
    dayjs,
    humanizeTutor(tutor = {}) {
      const firstName = tutor.firstName ? String(tutor.firstName).trim() : ''
      const lastName = tutor.lastName ? String(tutor.lastName).trim() : ''
      const fullName = [firstName, lastName].filter(Boolean).join(' ')

      return fullName || tutor.email || '-'
    },
    updateLocation(value) {
      this.$emit('update:selected-location', value)
    },
    updateTutor(value) {
      const parsed = typeof value === 'string' ? Number(value) : value
      this.$emit('update:selected-tutor-id', Number.isNaN(parsed) ? value : parsed)
    },
  },
}
</script>
